import { BaseUIService } from '@/modules/core/ui/services/BaseUIService';
import type { AuthUser } from '@/modules/auth/models/AuthUser';
import BroadcastService from '@/integrations/pusher/BroadcastService';
import { NotifyUserSocketEvent } from '@/modules/user/events/NotifyUserSocketEvent';
import SnackbarService from '@/modules/core/ui/services/SnackbarService';
import Lang from '@/modules/core/app/services/Lang';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import { UserNotifyType } from '@/modules/user/constants/UserNotifyType';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { getters } from '@/modules/core/app/helpers/store';
import { AuthMismatchEvent } from '@/modules/auth/constants/AuthMismatchEvent';
import { AuthRoute } from '@/modules/auth/routes/auth.routes';
// eslint-disable-next-line import/no-cycle
import AuthService from '@/modules/auth/services/AuthService';
import { AuthUserEvent } from '@/modules/auth/events/AuthUserEvent';

export class AuthUserBackgroundUIService extends BaseUIService {
  authUser?: AuthUser | null;

  private userNotifyTypes: Set<UserNotifyType> = new Set([
    UserNotifyType.REFRESH_PAGE,
    UserNotifyType.REFRESH_PAGE_FORCEFULLY,
    UserNotifyType.INVALIDATE_SESSION
  ]);

  private didSaveUser: boolean = false;

  initialize(user?: AuthUser): void {
    if (!user) {
      return;
    }

    this.authUser = user;
    BroadcastService.listen(NotifyUserSocketEvent, this.onNotify.bind(this), this.authUser.id);
    EventBus.listen<AuthUser>(AuthUserEvent.USER_CHANGED, this.onUserChanged.bind(this));

    if (this.authUser.permission?.canAccessDetectAuthSessionMismatch) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkForSession();
        }
      });
      this.checkForSessionPeriodically();
    }
  }

  shouldNotify(value: UserNotifyType): void {
    this.userNotifyTypes.add(value);
  }

  shouldSilence(value: UserNotifyType): void {
    this.userNotifyTypes.delete(value);
  }

  reInitialize(user: AuthUser): void {
    this.reset();
    this.initialize(user);
    this.resetNotifyTypes();
  }

  resetNotifyTypes(): void {
    this.userNotifyTypes = new Set([
      UserNotifyType.REFRESH_PAGE,
      UserNotifyType.REFRESH_PAGE_FORCEFULLY,
      UserNotifyType.INVALIDATE_SESSION
    ]);
  }

  onUserChanged(data?: AuthUser): void {
    this.didSaveUser = true;

    localStorage.setItem('last_active_user_id', data?.id ? data.id.toString() : '');
    localStorage.setItem(
      'last_active_estate_id',
      data?.currentEstate?.id ? data.currentEstate.id.toString() : ''
    );
  }

  onNotify(event: NotifyUserSocketEvent): void {
    if (!this.userNotifyTypes.has(event.type as UserNotifyType)) {
      return;
    }

    switch (event.type) {
      case UserNotifyType.INVALIDATE_SESSION:
        this.invalidateSession();
        return;
      case UserNotifyType.REFRESH_PAGE_FORCEFULLY:
        return this.refreshApp();
      case UserNotifyType.REFRESH_PAGE:
        return this.showRefreshSnackbar(event.message);
      default:
        return;
    }
  }

  refreshApp(): void {
    setTimeout(() => {
      DocumentUIService.reloadApp();
    }, 1000);
  }

  async invalidateSession(): Promise<void> {
    try {
      await AuthService.logout();
    } finally {
      DocumentUIService.reloadAppAtPath(AuthRoute.LOGIN.path);
    }
  }

  showRefreshSnackbar(message: string | undefined): void {
    const snackbarMessage: string = `<div><i class="cls cls-information-circle has-text-warning is-size-3"></div></i>
            <div>${message} </div>`;
    SnackbarService.warn(snackbarMessage, {
      position: 'is-top',
      indefinite: true,
      actionText: Lang.t('auth.refresh-page'),
      onAction: DocumentUIService.reloadApp
    });
  }

  reset(): void {
    BroadcastService.stopListening(NotifyUserSocketEvent, undefined, this.authUser?.id);
    this.authUser = null;
    this.resetNotifyTypes();
  }

  private checkForSessionPeriodically(): void {
    setTimeout(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      this.checkForSession();
      this.checkForSessionPeriodically();
    }, 5000);
  }

  private checkForSession(): void {
    if (!this.didSaveUser) {
      return;
    }

    const userId: string | null = localStorage.getItem('last_active_user_id');

    if (userId === null) {
      return;
    }

    if (!getters.auth.getUser()?.id) {
      return;
    }

    const authUser: AuthUser | null = getters.auth.getUser();
    const currentUserId: string = authUser?.id ? authUser!.id!.toString() : '';

    if (userId !== currentUserId) {
      EventBus.signal(AuthMismatchEvent.TOKEN_MISMATCH);
      return;
    }

    const estateId: string | null = localStorage.getItem('last_active_estate_id');
    if (estateId === null) {
      return;
    }

    const currentEstateId: string = getters.auth.getUser()?.currentEstate?.id
      ? authUser!.currentEstate!.id!.toString()
      : '';
    if (estateId !== currentEstateId && !authUser!.isViewingEstateDetails) {
      EventBus.signal(AuthMismatchEvent.TOKEN_MISMATCH);
      return;
    }
  }
}

export default new AuthUserBackgroundUIService();
